import { features, reviewPros, reviewCons, shippingMethods } from '@/mocks/homeData';

export function WhatIsSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
              What Is This?
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              What Is a Sugargoo Spreadsheet?
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
              A <strong>Sugargoo Spreadsheet</strong> is a structured product discovery system that helps shoppers find products faster. Instead of spending hours searching different marketplaces manually, users can access organized product collections containing product categories, price references, quality checking tips, and useful links — all in one convenient format.
            </p>

            <div className="space-y-3">
              {[
                { icon: 'ri-folder-5-line', text: 'Organized product categories with detailed information' },
                { icon: 'ri-price-tag-3-line', text: 'Price references and budget-friendly shopping notes' },
                { icon: 'ri-check-double-line', text: 'Quality checking tips and inspection guidance' },
                { icon: 'ri-link-m', text: 'Direct links to product listings and resources' },
              ].map((item) => (
                <div key={item.text} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100/80 text-accent-600 shrink-0 mt-0.5">
                    <i className={`${item.icon} w-4 h-4 flex items-center justify-center`}></i>
                  </div>
                  <span className="text-sm text-foreground-700 pt-1">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/60 bg-background-100/50">
                    <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Feature</th>
                    <th className="text-left px-5 py-3.5 font-semibold text-foreground-800">Manual Search</th>
                    <th className="text-left px-5 py-3.5 font-semibold text-primary-600">Sugargoo Spreadsheet</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Product Discovery', 'Slow searching', 'Organized collections'],
                    ['Time Required', 'Hours', 'Minutes'],
                    ['Product Comparison', 'Difficult', 'Simple'],
                    ['Beginner Friendly', 'Low', 'High'],
                  ].map((row) => (
                    <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                      <td className="px-5 py-3 text-foreground-700 font-medium">{row[0]}</td>
                      <td className="px-5 py-3 text-foreground-500">{row[1]}</td>
                      <td className="px-5 py-3 text-primary-600 font-medium">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function WhyUseSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
            Why Choose Us
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
            Why Use Our Sugargoo Spreadsheet?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Designed to make your shopping experience faster, smarter and safer
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {features.map((feat) => (
            <div
              key={feat.title}
              className="group p-5 md:p-6 bg-background-50 border border-background-200/60 rounded-2xl hover:border-accent-200 transition-all duration-200"
            >
              <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-accent-100/80 text-accent-500 mb-4 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-200">
                <i className={`${feat.icon} w-5 h-5 md:w-6 md:h-6 flex items-center justify-center`}></i>
              </div>
              <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2">
                {feat.title}
              </h3>
              <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                {feat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ReviewSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
            Sugargoo Review
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
            Sugargoo Review: Is Sugargoo Worth Using?
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            An honest, balanced look at what Sugargoo offers for international shoppers
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          <div className="bg-background-50 rounded-2xl border border-background-200/60 p-5 md:p-7">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                <i className="ri-thumb-up-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-900">Pros</h3>
            </div>
            <ul className="space-y-3">
              {reviewPros.map((pro) => (
                <li key={pro} className="flex items-start gap-3">
                  <i className="ri-check-line w-4 h-4 flex items-center justify-center text-accent-500 mt-0.5 shrink-0"></i>
                  <span className="text-sm text-foreground-700">{pro}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-background-50 rounded-2xl border border-background-200/60 p-5 md:p-7">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                <i className="ri-information-line w-4 h-4 flex items-center justify-center"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-900">Cons</h3>
            </div>
            <ul className="space-y-3">
              {reviewCons.map((con) => (
                <li key={con} className="flex items-start gap-3">
                  <i className="ri-close-line w-4 h-4 flex items-center justify-center text-secondary-500 mt-0.5 shrink-0"></i>
                  <span className="text-sm text-foreground-700">{con}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-8 p-5 md:p-7 bg-background-50 rounded-2xl border border-background-200/60">
          <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-4">Quick Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/60">
                  <th className="text-left px-4 py-3 font-semibold text-foreground-800">Feature</th>
                  <th className="text-left px-4 py-3 font-semibold text-primary-600">Sugargoo</th>
                  <th className="text-left px-4 py-3 font-semibold text-foreground-500">Other Agents</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Warehouse Service', 'Comprehensive', 'Varies'],
                  ['QC Photos', 'Included', 'May cost extra'],
                  ['Shipping Options', 'Multiple methods', 'Limited options'],
                  ['User Experience', 'Modern platform', 'Mixed'],
                  ['Pricing', 'Competitive', 'Varies widely'],
                ].map((row) => (
                  <tr key={row[0]} className="border-b border-background-200/40 last:border-0">
                    <td className="px-4 py-3 text-foreground-700 font-medium">{row[0]}</td>
                    <td className="px-4 py-3 text-primary-600">{row[1]}</td>
                    <td className="px-4 py-3 text-foreground-500">{row[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function LegitSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
              Trust & Safety
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              Is Sugargoo Legit?
            </h2>
            <p className="text-sm md:text-base text-foreground-600">
              Everything you need to know about the safety and reliability of Sugargoo
            </p>
          </div>

          <div className="bg-background-100/60 rounded-2xl border border-background-200/60 p-5 md:p-7">
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5">
              <strong>Sugargoo</strong> is a popular shopping agent platform used by international buyers worldwide. The platform has established processes to help protect buyers throughout the shopping journey. Here is how it works:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { title: 'Agent Service Model', desc: 'Sugargoo acts as your purchasing agent, buying products on your behalf from various marketplaces and handling all logistics.' },
                { title: 'Warehouse Process', desc: 'All items are shipped to the Sugargoo warehouse first, where staff inspect and photograph them before international shipping.' },
                { title: 'Payment Protection', desc: 'Payments are processed through the platform with order tracking and dispute resolution mechanisms available.' },
                { title: 'Risk Management', desc: 'QC photos help identify issues before shipping. You can request returns or exchanges for problematic items while they are still in the warehouse.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100/80 text-primary-500 shrink-0 mt-0.5">
                    <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-800 mb-1">{item.title}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function ShippingSection() {
  return (
    <section className="py-16 md:py-20 bg-background-100/60">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3">
            Shipping Guide
          </span>
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
            Sugargoo Shipping Cost Guide
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Understand how shipping costs are calculated and compare different shipping methods
          </p>
        </div>

        <div className="max-w-3xl mx-auto">
          <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-6 text-center">
            Shipping cost depends on <strong>package weight</strong>, <strong>destination country</strong>, <strong>shipping method</strong>, and <strong>product type</strong>. Consolidating multiple items into one package can significantly reduce per-item shipping costs.
          </p>

          <div className="bg-background-50 rounded-2xl border border-background-200/60 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200/60 bg-background-100/50">
                  <th className="text-left px-4 md:px-5 py-3.5 font-semibold text-foreground-800">Shipping Method</th>
                  <th className="text-left px-4 md:px-5 py-3.5 font-semibold text-foreground-800">Speed</th>
                  <th className="text-left px-4 md:px-5 py-3.5 font-semibold text-foreground-800">Est. Cost</th>
                  <th className="text-left px-4 md:px-5 py-3.5 font-semibold text-foreground-800">Best For</th>
                </tr>
              </thead>
              <tbody>
                {shippingMethods.map((item) => (
                  <tr key={item.method} className="border-b border-background-200/40 last:border-0 hover:bg-background-100/40 transition-colors duration-150">
                    <td className="px-4 md:px-5 py-3 font-medium text-foreground-800">{item.method}</td>
                    <td className="px-4 md:px-5 py-3 text-foreground-600">{item.speed}</td>
                    <td className="px-4 md:px-5 py-3 text-foreground-600">{item.cost}</td>
                    <td className="px-4 md:px-5 py-3 text-foreground-600">{item.bestFor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CouponSection() {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
          <div className="flex-1 text-center lg:text-left">
            <span className="inline-block px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full mb-3">
              Save Money
            </span>
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-3">
              Sugargoo Coupon & Discount Codes
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
              Sugargoo occasionally offers promotional coupons and discount codes for shipping fees or service charges. These promotions are typically announced on their official platform. Here are some tips to save money on your orders:
            </p>

            <div className="space-y-3">
              {[
                'Check Sugargoo official announcements regularly for new promotions',
                'Consolidate multiple items to reduce per-item shipping costs',
                'Choose economy shipping methods when not in a hurry',
                'Follow Sugargoo social media channels for exclusive deals',
              ].map((tip, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 shrink-0 mt-0.5">
                    <span className="text-xs font-semibold">{i + 1}</span>
                  </div>
                  <span className="text-sm text-foreground-700 pt-0.5">{tip}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 w-full">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20shopping%20gift%20voucher%20and%20elegant%20discount%20card%20on%20warm%20marble%20surface%20with%20soft%20natural%20lighting%2C%20premium%20promotional%20concept%20with%20gold%20accents%2C%20clean%20editorial%20product%20photography%20with%20neutral%20warm%20tones%20and%20minimalist%20composition&width=800&height=600&seq=coupon-sugargoo-v2&orientation=landscape"
                alt="Sugargoo Coupon and Discount Guide"
                className="w-full h-64 md:h-80 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}